import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays, MapPin, Video } from 'lucide-react';
import { MAIL_API, mailAuth, PANEL } from './mailUtils';

const dayKey = (iso) => (iso || '').slice(0, 10);
const fmtT = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const dayLabel = (k, today) => {
  if (k === today) return 'Today';
  const d = new Date(`${k}T00:00:00`);
  const t = new Date(`${today}T00:00:00`);
  if (d - t === 86400000) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

export default function CalendarStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${MAIL_API}/calendar`, { ...mailAuth(), params: { days: 7 } }).then(r => alive && setData(r.data))
      .catch(e => { if (!alive) return; if (e.response?.status === 403) setErr(true); else setTimeout(load, 4000); });
    load();
    return () => { alive = false; };
  }, []);
  if (err || (data && !data.events)) return null;

  const groups = {};
  (data?.events || []).forEach(e => { (groups[dayKey(e.start)] ||= []).push(e); });
  const keys = Object.keys(groups).sort();
  const now = Date.now();

  return (
    <div className={`${PANEL} overflow-hidden`} data-testid="calendar-strip">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center"><CalendarDays className="w-4 h-4 text-emerald-700" /></div>
        <div><h3 className="text-sm font-black text-slate-900">This week</h3><p className="text-[11px] text-slate-500">{data ? `${data.events.length} event${data.events.length === 1 ? '' : 's'} · next 7 days` : 'Loading…'}</p></div>
        <a href="https://calendar.google.com" target="_blank" rel="noreferrer" className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 underline" data-testid="calendar-open">Open calendar</a>
      </div>
      <div className="max-h-[320px] overflow-y-auto border-t border-slate-200/60 px-3 py-2 space-y-2">
        {data && keys.length === 0 && <p className="text-sm text-slate-400 py-4 text-center" data-testid="calendar-empty">Clear week — nothing booked</p>}
        {keys.map(k => (
          <div key={k} data-testid={`calendar-day-${k}`}>
            <p className={`text-[10px] font-black uppercase tracking-[0.18em] px-1 mb-1 ${k === data.today ? 'text-emerald-700' : 'text-slate-500'}`}>{dayLabel(k, data.today)}</p>
            {groups[k].map(e => {
              const past = !e.all_day && new Date(e.end) < now;
              return (
                <a key={e.id || e.start + e.summary} href={e.link || '#'} target="_blank" rel="noreferrer" className={`flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-white ${past ? 'opacity-50' : ''}`} data-testid={`calendar-event-${e.id}`}>
                  <span className="text-[11px] font-mono text-slate-500 w-16 shrink-0 pt-0.5 tabular-nums">{e.all_day ? 'all day' : fmtT(e.start)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-900 truncate">{e.summary}</span>
                    {(e.location || e.meet) && <span className="flex items-center gap-1 text-[11px] text-slate-500 truncate">{e.meet ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}{e.location || 'Google Meet'}</span>}
                  </span>
                </a>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
